import Box from '@app/components/common/Box';
import Icon from '@app/components/common/Icon';
import Button from '@app/components/common/Button';
import Divider from '@app/components/common/Divider';
import List from '@app/components/common/List';
import ListItem from '@app/components/common/List/ListItem';
import useRootSelector from '@app/hooks/useRootSelector';
import useScrollRestoration from '@app/hooks/useScrollRestoration';
import { AnimatePresence, m, useAnimate } from 'framer-motion';
import { useLocation, useParams } from 'react-router-dom';
import { TCsx, useTheme } from '@emotion/react';
import Typography from '@app/components/common/Typography';
import {
  actionCreatorsMenu,
  BreadCrumbAction,
  BUCKET_TEXT_DICTIONARY,
  ITEM_INITIAL_STATE,
  SectionId,
} from '@westondev/tableturn-core';
import { useSelector } from 'react-redux';
import { selectIsMassUpdate } from '@app/state/menu/menuSelectors';
import { getPathWithOrgData } from '@app/helpers/navigation';
import { bindActionCreators } from '@reduxjs/toolkit';
import { useAppDispatch } from '@app/state/store';
import {
  BUCKET_PATH_INDEX,
  MENU_SCREEN_BY_BUCKET_NAME,
  MODULE_PATH_INDEX,
} from '@app/constants';
import useNavigateWithOrg from '@app/hooks/useNavigateWithOrg';
import {
  resetPathsByBucketNavigation,
  resetPathsByMainPath,
} from '@app/helpers/restoration';
import { useEffect, useMemo, useRef, useState } from 'react';
import { itemDetailsListItemActiveStyles } from '@app/components/common/List/ListItem/styles';
import { IScrollSideBar, ITab } from './types';
import { MENU_SIDE_BAR_TABS_BY_BUCKET } from './constants';
import { ParseKeys } from 'i18next';
import { actionCreatorsMenuWeb } from '@app/state';
import useRefreshMenuWeb from '@app/hooks/useRefreshMenuWeb';

const ScrollSideBar = ({ t, showDropdown, bucketId }: IScrollSideBar) => {
  const dispatch = useAppDispatch();
  const checkForChangesAndNavigateWeb = bindActionCreators(
    actionCreatorsMenuWeb.checkForChangesAndNavigateWeb,
    dispatch,
  );

  const isMassUpdate = useSelector(selectIsMassUpdate);

  const setBreadCrumb = bindActionCreators(
    actionCreatorsMenu.setBreadCrumb,
    dispatch,
  );

  const theme = useTheme();
  const navigate = useNavigateWithOrg();
  const location = useLocation();
  const { id: elementId } = useParams();
  const itemName = useRef<string>('');
  const itemPath = useRef<string>('');
  const bucketIdLabel = useRef(bucketId ? bucketId : 'menuTypes');
  const currentBucketDictionary = useRef<ITab[]>([] as ITab[]);

  const { saveScrollPosition } = useScrollRestoration('layout-content');

  const [activeTab, setActiveTab] = useState(SectionId.BASIC);

  useEffect(() => {
    if (bucketId) bucketIdLabel.current = bucketId;
    setActiveTab(SectionId.BASIC);
  }, [bucketId]);

  useEffect(() => {
    if (bucketId)
      currentBucketDictionary.current = MENU_SIDE_BAR_TABS_BY_BUCKET[bucketId];
  }, [bucketId]);

  const currentPath = location.state?.currentPath || location.pathname;
  const newId = useRootSelector(state => state.menu.changeData.data?.id ?? 0);
  const currentItem = useRootSelector(
    state =>
      state.menu.selectedMode.currentMenu[bucketId ?? 'menuTypes'][
        Number(elementId) || Number(newId)
      ] ?? ITEM_INITIAL_STATE,
  );

  useEffect(() => {
    if (currentItem?.name !== '') {
      itemName.current = currentItem.name;
    } else if (bucketId) {
      itemName.current = `${t('commonTexts.adding')} ${t(
        `menuScreen.bucketElement.${bucketId}` as ParseKeys,
      )}`;
    }
  }, [bucketId, currentItem, t]);

  useEffect(() => {
    if (bucketId) {
      itemPath.current = currentPath;
    } else {
      saveScrollPosition(0, itemPath.current);
      itemPath.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketId]);

  const scrollSideBarStyles: TCsx = useMemo(
    () => ({
      backgroundColor: theme.colors.white,
      zIndex: 9999,
      position: 'absolute',
      top: showDropdown ? 71 : 0,
      width: 'calc(100% - 24px)',
      overflowX: 'hidden',
      overflowY: 'auto',
      left: 0,
      height: `calc(100% - ${showDropdown ? 95 : 0}px)`,
      '@media (min-height: 1038px)': {
        paddingBottom: '80px !important',
      },
    }),
    [theme.colors.white, showDropdown],
  );

  const handleGoBack = () => {
    checkForChangesAndNavigateWeb(() => {
      const bucketPath = bucketId ? MENU_SCREEN_BY_BUCKET_NAME[bucketId] : '/';
      const newPath = getPathWithOrgData(bucketPath);

      if (bucketId)
        setBreadCrumb([
          {
            action: BreadCrumbAction.NAV_MAIN,
            text: BUCKET_TEXT_DICTIONARY[bucketId],
            onPress: () => navigate(MENU_SCREEN_BY_BUCKET_NAME[bucketId]),
          },
        ]);

      navigate(newPath);
      const currentModule = currentPath.split('/')[MODULE_PATH_INDEX];
      resetPathsByBucketNavigation(
        currentModule,
        newPath.split('/')[BUCKET_PATH_INDEX],
      );

      setActiveTab(SectionId.BASIC);
      if (currentModule && newPath.includes(currentModule)) return;
      resetPathsByMainPath('/menu/', newPath);
    }, useRefreshMenuWeb);
  };

  const handleClickTab = (id: SectionId) => {
    setActiveTab(id);
    const element = document.getElementById(id);
    const layoutContent = document.getElementById('layout-content');
    const rect = element?.getBoundingClientRect();
    const elementPosition =
      (rect?.top || 0) + (layoutContent?.scrollTop || 0) - 172;
    layoutContent?.scrollTo({
      behavior: 'smooth',
      top: elementPosition,
      left: 0,
    });
  };

  return (
    <AnimatePresence>
      <SideBarContainer
        isVisible={Boolean(bucketId)}
        containerCsx={scrollSideBarStyles}>
        <Box
          csx={{
            display: 'flex',
            alignItems: 'center',
            paddingRight: '10px',
          }}>
          <Button
            variant="primary"
            icon={<Icon size="25px" name="MdArrowBack" />}
            csx={{
              height: '40px',
              width: '65px',
              border: 'none',
              paddingRight: '10px',
              marginRight: '12px',
            }}
            onClick={handleGoBack}
          />

          <Box>
            <Typography fontWeight="bold">
              {t(`loggedIn.sideBar.tabs.${bucketIdLabel.current}` as ParseKeys)}
            </Typography>
            <Typography csx={{ fontStyle: 'italic' }}>
              {itemName.current}
            </Typography>
          </Box>
        </Box>
        <Divider csx={{ width: '100%', margin: '10px 0' }} />
        <List>
          {currentBucketDictionary.current &&
            currentBucketDictionary.current.map(({ id, hideOnMassUpdate }) =>
              isMassUpdate && hideOnMassUpdate ? null : (
                <ListItem
                  key={`itemDetailsSideBar-${id}`}
                  isActive={activeTab === id}
                  styleOverwrite={
                    activeTab === id ? itemDetailsListItemActiveStyles : {}
                  }
                  text={t(`menuScreen.sectionsTitles.${id}` as ParseKeys)}
                  onClick={() => handleClickTab(id)}
                  isLink={`#${id}`}
                />
              ),
            )}
        </List>
      </SideBarContainer>
    </AnimatePresence>
  );
};

export default ScrollSideBar;

const SideBarContainer = ({
  children,
  isVisible,
  containerCsx,
}: {
  children: React.ReactNode;
  isVisible: boolean;
  containerCsx: TCsx;
}) => {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const animateSidebar = async () => {
      if (isVisible) {
        await animate(
          scope.current,
          {
            transform: 'translateX(0)',
            width: 'calc(100% - 24px)',
            paddingInline: '12px',
            paddingTop: '10px',
            paddingBottom: '15px',
          },
          { duration: 0.3, ease: 'easeOut' },
        );
      } else {
        await animate(
          scope.current,
          {
            transform: 'translateX(-100%)',
            width: 'calc(100% - 24px)',
            paddingInline: '12px',
            paddingTop: '10px',
            paddingBottom: '15px',
          },
          { duration: 0.3 },
        );
      }
    };
    animateSidebar();
  }, [isVisible, animate, scope]);

  return (
    <m.div ref={scope} css={containerCsx} style={{ width: '0px' }}>
      {children}
    </m.div>
  );
};
